import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { Modal, Container, Row, Col, Button, Form } from "react-bootstrap";

import {
  useTriggerNotifyPcmPostedMutation,
  useTriggerNotifyPcmRejectedMutation,
} from "api/memberPortal/notifications/pcm.api";
import { useAuth } from "hooks/useAuth";

export default function FinalizePCMModal({
  show,
  handleShow,
  pcm,
  statuses,
  refetch,
  setAlert,
  pcmPps,
}) {
  const [reject, setReject] = useState(false);
  const [approve, setApprove] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [postingAdvice, setPostingAdvice] = useState("");
  const [amDate, setAmDate] = useState("");
  const [sendPostingAdvice, setSendPostingAdvice] = useState(true);
  const { authToken, csrfToken, currentUser } = useAuth();
  const [triggerNotifyPcmPosted] = useTriggerNotifyPcmPostedMutation();
  const [triggerNotifyPcmRejected] = useTriggerNotifyPcmRejectedMutation();

  useEffect(() => {
    if (show) {
      setApprove(false);
      setReject(false);
      setSendPostingAdvice(true);
      setRejectionReason("");
      setPostingAdvice("");
    }
  }, [show]);

  const triggerNotification = async () => {
    if (approve) {
      triggerNotifyPcmPosted({
        rid: pcm?.rid,
        postingAdvice: postingAdvice || undefined,
      });
    } else if (reject) {
      triggerNotifyPcmRejected({
        rid: pcm?.rid,
        rejectionReason,
      });
    }
  };

  const getAmDate = () => {
    //if pcm has certain type of pp, add 5 years, else add 2 years
    if (
      pcmPps?.find(
        (pp) =>
          pp?.ppid?.pp_name ===
          "Protection Profile for Peripheral Sharing Device Version 4.0",
      )
    ) {
      return moment.utc().add(5, "years").format("YYYY-MM-DD");
    } else if (pcm?.maintenance_id !== null) {
      return moment
        .utc(pcm?.maintenance_id?.am_date)
        .add(2, "years")
        .format("YYYY-MM-DD");
    } else {
      return moment.utc().add(2, "years").format("YYYY-MM-DD");
    }
  };

  const handleUpdateProductCC = async () => {
    await axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}pcm/products/update_productcc_for_maintenance/`,
        {
          ccid: pcm.ccid?.ccid,
          am_date: amDate !== "" ? amDate : getAmDate(),
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Token ${authToken}`,
            "X-CSRFToken": csrfToken,
          },
        },
      )
      .catch((error) => {
        console.log("unable to update productcc for maintenance", error);
      });
  };

  const updatePcm = async (e) => {
    e.preventDefault();
    let status = "";
    let concur_status = "Review in Progress";
    if (approve) {
      status = statuses?.find(
        (s) => s?.label.toLowerCase() === "posted",
      )?.value;
      concur_status = "Approved";
    } else if (reject) {
      status = statuses?.find(
        (s) => s?.label.toLowerCase() === "rejected",
      )?.value;
      concur_status = "Rejected";
    }
    let data = {
      stid: status,
      agent: currentUser.id,
      am_date: amDate !== "" ? amDate : getAmDate(),
      enddate: new Date(
        new Date().setMinutes(
          new Date().getMinutes() - new Date().getTimezoneOffset(),
        ),
      ),
      moddate: new Date(
        new Date().setMinutes(
          new Date().getMinutes() - new Date().getTimezoneOffset(),
        ),
      ),
      editor: currentUser.id,
    };

    if (pcm.mr_concur_status) {
      data["mr_concur_status"] = concur_status;
    } else {
      data["concur_status"] = concur_status;
    }

    await axios
      .put(`${import.meta.env.VITE_DJANGO_ENDPOINT}pcm/${pcm?.rid}/`, data, {
        withCredentials: true,
        headers: {
          Authorization: `Token ${authToken}`,
          "X-CSRFToken": csrfToken,
        },
      })
      .then(() => {
        if (approve) {
          handleUpdateProductCC(); //we want to update the product cc if approved
        }
        if (sendPostingAdvice) {
          triggerNotification();
        }
        refetch();
        handleShow();
        setAlert({ message: "PCM Updated!", type: "success" });
      })
      .catch((error) => {
        console.log("unable to update pcm", error);
        setAlert({ message: "Error Updating PCM.", type: "danger" });
      });
  };

  return (
    <Modal show={show} onHide={handleShow} size="lg">
      <Modal.Header className="border-0" closeButton>
        <h3 className="text-bright-navy">FINALIZE PCM - {pcm?.ticket}</h3>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Row className="mb-4">
            <Col className="d-flex justify-content-center">
              <h4 className="text-bright-navy">{pcm?.prod}</h4>
            </Col>
          </Row>
          <Row>
            <Form.Group controlId="team_due_date">
              <Form.Label>
                <p className="fw-bold">
                  Please Select Assurance Maintenance Date:
                </p>
              </Form.Label>
              <Form.Control
                className="mb-3 w-50"
                type="date"
                name="am_date"
                onChange={(e) => setAmDate(e.target.value)}
                defaultValue={getAmDate()}
              />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <p className="fw-bold">
              Please Select the Resolution for this Item:
            </p>
          </Row>
          <Row>
            <Col>
              <Row>
                <Col md={3}>
                  <Button
                    variant={reject ? "danger" : "outline-danger"}
                    onClick={() => {
                      setReject(true);
                      setApprove(false);
                    }}
                  >
                    Reject Request
                  </Button>
                </Col>
                <Col>
                  <p>
                    Choose "Reject Request" if the PCM Request is to be
                    cancelled.
                  </p>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <Row>
                <Col md={3}>
                  <Button
                    variant={approve ? "success" : "outline-success"}
                    onClick={() => {
                      setApprove(true);
                      setReject(false);
                    }}
                  >
                    Mark as Posted
                  </Button>
                </Col>
                <Col>
                  <p>
                    Choose &quot;Mark as Posted&quot; if the PCM Request is
                    fully resolved and the item and associated documents are
                    accepted for inclusion on the NIAP PCL.
                  </p>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <p className="fw-bold">Send Posting Advice to the Customer?</p>
          </Row>
          <Row>
            <Col>
              <Row>
                <Col md={2}>
                  <Button
                    variant={!sendPostingAdvice ? "danger" : "outline-danger"}
                    onClick={() => {
                      setSendPostingAdvice(false);
                    }}
                  >
                    No
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    variant={sendPostingAdvice ? "success" : "outline-success"}
                    onClick={() => {
                      setSendPostingAdvice(true);
                    }}
                  >
                    Yes
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
          {reject && (
            <Row className="mt-3">
              <Form.Group>
                <Form.Label className="fw-bold">Rejection Reason:</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  required
                  placeholder="Enter rejection reason..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                />
              </Form.Group>
            </Row>
          )}
          {sendPostingAdvice && approve && (
            <Row className="mt-3">
              <Form.Group>
                <Form.Label className="fw-bold">
                  Posting Advice to the Customer (optional):
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter posting advice..."
                  value={postingAdvice}
                  onChange={(e) => setPostingAdvice(e.target.value)}
                />
              </Form.Group>
            </Row>
          )}
        </Container>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Container>
          <Row className="d-flex justify-content-end">
            <Col sm={2}>
              <Button variant="outline-primary" onClick={handleShow}>
                Cancel
              </Button>
            </Col>
            <Col sm={3}>
              <Button
                variant="primary"
                onClick={updatePcm}
                disabled={
                  (approve === false && reject === false) ||
                  (reject && !rejectionReason.trim())
                }
              >
                Save and Send
              </Button>
            </Col>
          </Row>
        </Container>
      </Modal.Footer>
    </Modal>
  );
}
